/** Tracker-qualified campaign scope options; TC requires its automation capability.
 * For Media Devoted | Created By Ciro Autiero */

import { trackerLabel, type TagTrafficProvider, type TrackerId } from '@trafficpulse/shared';

import { dispatchTracker } from '@/hooks/campaign-tracker';
import { getCountryName, type Campaign, type CampaignProvider } from '@/hooks/voluum/useVoluum';
import { normScope, tagProviderOf, type OverrideUsage } from '@/features/tag-rule-scope/model';

import type { ScopeOption } from './combobox';

export interface CampaignRowScope {
  tracker: TrackerId;
  provider: TagTrafficProvider;
}

/** A campaign list row's tracker + tag provider; a row without a provider is Voluum (as listed). */
export function campaignRowScope(row: { provider?: CampaignProvider }): CampaignRowScope | null {
  const tracker = dispatchTracker(row.provider ?? 'voluum');
  const provider = tagProviderOf(tracker);
  return provider ? { tracker, provider } : null;
}

function campaignOption(c: Campaign, matches: boolean, inUse: OverrideUsage): ScopeOption[] {
  const scope = campaignRowScope(c);
  if (!scope) return [];
  const { tracker, provider } = scope;
  const id = String(c.id);
  const name = c.name?.trim() || id;
  const option: ScopeOption = {
    value: `${provider}:${id}`,
    label: name,
    provider: tracker,
    // The tracker is a badge now, so this column carries only the id.
    meta: id.length > 12 ? `${id.slice(0, 8)}…` : id,
    title: `${name} · ${trackerLabel(tracker)} · ${id}${matches ? '' : ' · country unknown on this tracker'}`,
    muted: !matches,
    count: inUse.campaigns.get(`${provider}:${normScope(id)}`) ?? 0,
    // Searchable by full id as well — campaigns are usually referenced by it.
    search: `${name} ${id} ${provider}`.toLowerCase(),
  };
  return [option];
}

export interface CampaignScopeInput {
  campaigns: readonly Campaign[];
  countries: ReadonlyArray<{ key: string; name: string }>;
  /** Normalised key of the chosen country; '' = none chosen (no campaign list). */
  countryKey: string;
  inUse: OverrideUsage;
}

/**
 * Campaigns for the chosen country, matching ones first.
 *
 * Campaigns whose country is UNKNOWN are kept, not hidden: Binom's list
 * endpoint carries no country at all (only its detail payload infers one from
 * geo rules), so filtering strictly would hide every Binom campaign and make
 * the picker useless on that tracker. A campaign that belongs to a different
 * KNOWN country is still excluded — that much we can say for sure. Overrides
 * key on (provider, id), never on country, so nothing here affects storage.
 */
export function campaignScopeOptions(input: CampaignScopeInput): ScopeOption[] {
  const { campaigns, countries, countryKey, inUse } = input;
  if (!countryKey) return [];
  const country = countries.find((c) => normScope(c.key) === countryKey);
  const want = normScope(country?.name ?? countryKey);
  const rows = campaigns.map((c) => {
    const raw = normScope(getCountryName(c.country));
    const known = Boolean(raw) && raw !== 'unknown';
    return { c, matches: known && raw === want, known };
  });
  return rows
    .filter((r) => r.matches || !r.known)
    .sort((a, b) => Number(b.matches) - Number(a.matches))
    .flatMap(({ c, matches }) => campaignOption(c, matches, inUse));
}
